import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { db, auth, storage } from "../../../firebase-config";
import { collection, addDoc, doc, getDoc } from "firebase/firestore"; // ✅ Fixed missing imports
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const AdminProductAdd = () => {
  const [product, setProduct] = useState({
    name: "",
    description: "",
    defaultPrice: "",
    category: "",
    imageUrl: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    const checkAdminStatus = async () => {
      try {
        const adminRef = doc(db, "admins", currentUser.uid); // ✅ Using `doc` correctly
        const adminSnap = await getDoc(adminRef); // ✅ Using `getDoc` correctly
        setIsAdmin(adminSnap.exists());
      } catch (err) {
        console.error("Error checking admin status:", err);
        setError("Failed to verify admin access.");
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  const handleInputChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleAddProduct = async () => {
    if (!isAdmin) {
      setError("You do not have permission to add products.");
      return;
    }

    if (!product.name || !product.defaultPrice || !product.category) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      let imageUrl = "";
      if (imageFile) {
        const storageRef = ref(storage, `productImages/${Date.now()}-${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const newProduct = {
        ...product,
        defaultPrice: parseFloat(product.defaultPrice),
        imageUrl: imageUrl || "https://via.placeholder.com/200",
        createdAt: new Date(),
      };

      await addDoc(collection(db, "products"), newProduct);

      alert("Product added successfully!");
      navigate("/admin/products");
    } catch (err) {
      console.error("Error adding product:", err);
      setError("Failed to add product.");
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return <Typography color="error">Access Denied</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Add New Product
      </Typography>

      {error && <Typography color="error">{error}</Typography>}

      <TextField
        label="Product Name"
        variant="outlined"
        fullWidth
        name="name"
        value={product.name}
        onChange={handleInputChange}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Description"
        variant="outlined"
        fullWidth
        multiline
        rows={3}
        name="description"
        value={product.description}
        onChange={handleInputChange}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Price"
        variant="outlined"
        fullWidth
        type="number"
        name="defaultPrice"
        value={product.defaultPrice}
        onChange={handleInputChange}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Category</InputLabel>
        <Select name="category" value={product.category} onChange={handleInputChange}>
          <MenuItem value="Electronics">Electronics</MenuItem>
          <MenuItem value="Furniture">Furniture</MenuItem>
          <MenuItem value="Clothing">Clothing</MenuItem>
        </Select>
      </FormControl>

      <Button variant="contained" component="label" sx={{ mb: 2 }}>
        Upload Image
        <input type="file" hidden accept="image/*" onChange={handleImageChange} />
      </Button>

      <Box sx={{ mb: 2 }}>
        {imageFile && <Typography>Selected Image: {imageFile.name}</Typography>}
      </Box>

      <Button variant="contained" color="primary" onClick={handleAddProduct} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : "Add Product"}
      </Button>
    </Container>
  );
};

export default AdminProductAdd;
